import { useEffect, useState, useCallback, useRef } from 'react';
import { useSocket } from '@/contexts/SocketContext';

/**
 * Workflow Test Events
 * Events emitted by the WorkflowSocketServer during test execution
 */
export const WORKFLOW_TEST_EVENTS = {
  EXECUTION_STARTED: 'execution:started',
  EXECUTION_COMPLETED: 'execution:completed',
  EXECUTION_FAILED: 'execution:failed',
  NODE_STARTED: 'node:started',
  NODE_COMPLETED: 'node:completed',
  NODE_FAILED: 'node:failed',
  NODE_SKIPPED: 'node:skipped',
} as const;

/**
 * Node execution status for UI rendering
 */
export type NodeExecutionStatus = 'idle' | 'running' | 'completed' | 'failed' | 'skipped';

/**
 * Event data types
 */
export interface ExecutionStartedEvent {
  executionId: string;
  workflowId: string;
  workflowName: string;
  totalNodes: number;
  timestamp: string;
}

export interface ExecutionCompletedEvent {
  executionId: string;
  status: 'completed' | 'failed';
  duration: number;
  nodesExecuted: number;
  nodesFailed: number;
  nodesSkipped: number;
  timestamp: string;
}

export interface ExecutionFailedEvent {
  executionId: string;
  error: string;
  failedNodeId?: string;
  timestamp: string;
}

export interface NodeStartedEvent {
  executionId: string;
  nodeId: string;
  nodeType: string;
  nodeLabel?: string;
  timestamp: string;
}

export interface NodeCompletedEvent {
  executionId: string;
  nodeId: string;
  status: 'completed';
  output?: any;
  duration: number;
  timestamp: string;
}

export interface NodeFailedEvent {
  executionId: string;
  nodeId: string;
  status: 'failed';
  error: string;
  duration: number;
  timestamp: string;
}

export interface NodeSkippedEvent {
  executionId: string;
  nodeId: string;
  status: 'skipped';
  reason: string;
  /** Payload bruto do `bail` (`{ skipped, reason, nodeId }`) — I1 (Fase 3):
   * sem isso a tela do teste ao vivo não tinha como ler `info.output?.reason`. */
  output?: unknown;
  timestamp: string;
}

/**
 * Node status with details for UI
 */
export interface NodeStatusInfo {
  nodeId: string;
  status: NodeExecutionStatus;
  nodeType?: string;
  nodeLabel?: string;
  output?: any;
  error?: string;
  duration?: number;
  timestamp?: string;
  order?: number;
}

/**
 * Test execution progress state
 */
export interface TestProgress {
  isRunning: boolean;
  executionId: string | null;
  workflowId: string | null;
  workflowName: string | null;
  totalNodes: number;
  nodesExecuted: number;
  nodesFailed: number;
  nodesSkipped: number;
  currentNodeId: string | null;
  status: 'idle' | 'running' | 'completed' | 'failed';
  error: string | null;
  duration: number | null;
  startedAt: string | null;
  completedAt: string | null;
}

/**
 * Hook return type
 */
export interface UseWorkflowTestSocketReturn {
  isConnected: boolean;
  testProgress: TestProgress;
  nodeStatuses: Map<string, NodeStatusInfo>;
  joinTestRoom: (workflowId: string, executionId: string) => void;
  leaveTestRoom: () => void;
  resetTest: () => void;
  getNodeStatus: (nodeId: string) => NodeStatusInfo | undefined;
}

/**
 * Initial progress state
 */
const initialProgress: TestProgress = {
  isRunning: false,
  executionId: null,
  workflowId: null,
  workflowName: null,
  totalNodes: 0,
  nodesExecuted: 0,
  nodesFailed: 0,
  nodesSkipped: 0,
  currentNodeId: null,
  status: 'idle',
  error: null,
  duration: null,
  startedAt: null,
  completedAt: null,
};

/**
 * useWorkflowTestSocket
 *
 * Hook for real-time workflow test execution progress via Socket.IO.
 * Uses the existing SocketContext connection and manages test room subscriptions.
 *
 * Features:
 * - Uses existing socket connection from SocketContext
 * - Room-based isolation for concurrent tests
 * - Real-time progress updates for execution and individual nodes
 * - Node status tracking with detailed information
 *
 * @example
 * ```tsx
 * const {
 *   isConnected,
 *   testProgress,
 *   nodeStatuses,
 *   joinTestRoom,
 *   leaveTestRoom,
 *   resetTest,
 *   getNodeStatus
 * } = useWorkflowTestSocket();
 *
 * // After starting test via API
 * joinTestRoom(workflowId, executionId);
 *
 * // Check node status
 * const nodeStatus = getNodeStatus(nodeId);
 *
 * // Cleanup
 * leaveTestRoom();
 * ```
 */
export const useWorkflowTestSocket = (): UseWorkflowTestSocketReturn => {
  // Use existing socket from SocketContext
  const { socket, isConnected } = useSocket();
  const [testProgress, setTestProgress] = useState<TestProgress>(initialProgress);
  const [nodeStatuses, setNodeStatuses] = useState<Map<string, NodeStatusInfo>>(new Map());

  // Track current room for cleanup
  const currentRoomRef = useRef<{ workflowId: string; executionId: string } | null>(null);

  /**
   * Set up workflow test event listeners on the existing socket
   */
  useEffect(() => {
    if (!socket || !isConnected) {
      return;
    }

    console.log('✅ [WorkflowTestSocket] Setting up workflow test event listeners on existing socket');

    // Execution events
    const handleExecutionStarted = (data: ExecutionStartedEvent) => {
      console.log('🚀 [WorkflowTestSocket] Execution started:', data);
      setTestProgress((prev) => ({
        ...prev,
        isRunning: true,
        executionId: data.executionId,
        workflowId: data.workflowId,
        workflowName: data.workflowName,
        totalNodes: data.totalNodes,
        status: 'running',
        startedAt: data.timestamp,
      }));
    };

    const handleExecutionCompleted = (data: ExecutionCompletedEvent) => {
      console.log('✅ [WorkflowTestSocket] Execution completed:', data);
      setTestProgress((prev) => ({
        ...prev,
        isRunning: false,
        status: data.status === 'completed' ? 'completed' : 'failed',
        nodesExecuted: data.nodesExecuted,
        nodesFailed: data.nodesFailed,
        nodesSkipped: data.nodesSkipped,
        duration: data.duration,
        completedAt: data.timestamp,
        currentNodeId: null,
      }));
    };

    const handleExecutionFailed = (data: ExecutionFailedEvent) => {
      console.error('❌ [WorkflowTestSocket] Execution failed:', data);
      setTestProgress((prev) => ({
        ...prev,
        isRunning: false,
        status: 'failed',
        error: data.error,
        completedAt: data.timestamp,
        currentNodeId: data.failedNodeId || null,
      }));
    };

    // Node events
    const handleNodeStarted = (data: NodeStartedEvent) => {
      console.log('▶️ [WorkflowTestSocket] Node started:', data.nodeId);
      setTestProgress((prev) => ({
        ...prev,
        currentNodeId: data.nodeId,
      }));
      setNodeStatuses((prev) => {
        const updated = new Map(prev);
        updated.set(data.nodeId, {
          nodeId: data.nodeId,
          status: 'running',
          nodeType: data.nodeType,
          nodeLabel: data.nodeLabel,
          timestamp: data.timestamp,
          order: (data as any).order,
        });
        return updated;
      });
    };

    const handleNodeCompleted = (data: NodeCompletedEvent) => {
      console.log('✅ [WorkflowTestSocket] Node completed:', data.nodeId);
      setTestProgress((prev) => ({
        ...prev,
        nodesExecuted: prev.nodesExecuted + 1,
      }));
      setNodeStatuses((prev) => {
        const updated = new Map(prev);
        const existing = updated.get(data.nodeId);
        updated.set(data.nodeId, {
          ...existing,
          nodeId: data.nodeId,
          status: 'completed',
          output: data.output,
          duration: data.duration,
          timestamp: data.timestamp,
          order: (data as any).order ?? existing?.order,
        });
        return updated;
      });
    };

    const handleNodeFailed = (data: NodeFailedEvent) => {
      console.error('❌ [WorkflowTestSocket] Node failed:', data.nodeId, data.error);
      setTestProgress((prev) => ({
        ...prev,
        nodesFailed: prev.nodesFailed + 1,
      }));
      setNodeStatuses((prev) => {
        const updated = new Map(prev);
        const existing = updated.get(data.nodeId);
        updated.set(data.nodeId, {
          ...existing,
          nodeId: data.nodeId,
          status: 'failed',
          error: data.error,
          duration: data.duration,
          timestamp: data.timestamp,
          order: (data as any).order ?? existing?.order,
        });
        return updated;
      });
    };

    const handleNodeSkipped = (data: NodeSkippedEvent) => {
      console.log('⏭️ [WorkflowTestSocket] Node skipped:', data.nodeId, data.reason);
      setTestProgress((prev) => ({
        ...prev,
        nodesSkipped: prev.nodesSkipped + 1,
      }));
      setNodeStatuses((prev) => {
        const updated = new Map(prev);
        updated.set(data.nodeId, {
          nodeId: data.nodeId,
          status: 'skipped',
          error: data.reason,
          // I1 (Fase 3): guarda também o payload bruto do `bail` — é dele que
          // `WorkflowEditorPage` lê `info.output?.reason` no ramo de teste ao vivo.
          output: data.output,
          timestamp: data.timestamp,
          order: (data as any).order,
        });
        return updated;
      });
    };

    // Register event listeners
    socket.on(WORKFLOW_TEST_EVENTS.EXECUTION_STARTED, handleExecutionStarted);
    socket.on(WORKFLOW_TEST_EVENTS.EXECUTION_COMPLETED, handleExecutionCompleted);
    socket.on(WORKFLOW_TEST_EVENTS.EXECUTION_FAILED, handleExecutionFailed);
    socket.on(WORKFLOW_TEST_EVENTS.NODE_STARTED, handleNodeStarted);
    socket.on(WORKFLOW_TEST_EVENTS.NODE_COMPLETED, handleNodeCompleted);
    socket.on(WORKFLOW_TEST_EVENTS.NODE_FAILED, handleNodeFailed);
    socket.on(WORKFLOW_TEST_EVENTS.NODE_SKIPPED, handleNodeSkipped);

    // Cleanup event listeners on unmount
    return () => {
      console.log('🔌 [WorkflowTestSocket] Removing workflow test event listeners');
      socket.off(WORKFLOW_TEST_EVENTS.EXECUTION_STARTED, handleExecutionStarted);
      socket.off(WORKFLOW_TEST_EVENTS.EXECUTION_COMPLETED, handleExecutionCompleted);
      socket.off(WORKFLOW_TEST_EVENTS.EXECUTION_FAILED, handleExecutionFailed);
      socket.off(WORKFLOW_TEST_EVENTS.NODE_STARTED, handleNodeStarted);
      socket.off(WORKFLOW_TEST_EVENTS.NODE_COMPLETED, handleNodeCompleted);
      socket.off(WORKFLOW_TEST_EVENTS.NODE_FAILED, handleNodeFailed);
      socket.off(WORKFLOW_TEST_EVENTS.NODE_SKIPPED, handleNodeSkipped);
    };
  }, [socket, isConnected]);

  /**
   * Join a test room for a specific workflow execution
   */
  const joinTestRoom = useCallback(
    (workflowId: string, executionId: string) => {
      if (!socket || !isConnected) {
        console.warn('[WorkflowTestSocket] Cannot join room - not connected');
        return;
      }

      // Leave current room if any
      if (currentRoomRef.current) {
        socket.emit('leave:test', currentRoomRef.current);
      }

      // Join new room
      socket.emit('join:test', { workflowId, executionId });
      currentRoomRef.current = { workflowId, executionId };

      console.log(`📥 [WorkflowTestSocket] Joined test room: test:${workflowId}:${executionId}`);
    },
    [socket, isConnected]
  );

  /**
   * Leave the current test room
   */
  const leaveTestRoom = useCallback(() => {
    if (!socket || !currentRoomRef.current) {
      return;
    }

    socket.emit('leave:test', currentRoomRef.current);
    console.log(
      `📤 [WorkflowTestSocket] Left test room: test:${currentRoomRef.current.workflowId}:${currentRoomRef.current.executionId}`
    );
    currentRoomRef.current = null;
  }, [socket]);

  /**
   * Reset test state
   */
  const resetTest = useCallback(() => {
    setTestProgress(initialProgress);
    setNodeStatuses(new Map());
    leaveTestRoom();
  }, [leaveTestRoom]);

  /**
   * Get status for a specific node
   */
  const getNodeStatus = useCallback(
    (nodeId: string): NodeStatusInfo | undefined => {
      return nodeStatuses.get(nodeId);
    },
    [nodeStatuses]
  );

  return {
    isConnected,
    testProgress,
    nodeStatuses,
    joinTestRoom,
    leaveTestRoom,
    resetTest,
    getNodeStatus,
  };
};

export default useWorkflowTestSocket;
